import React, { useEffect, useState } from 'react';

// The table an author gets by clicking a bar: who answered, and what they said.
//
// It began as part of the classified-text chart and is shared verbatim by the
// scalar, set-member and boolean canvases, so a bar means the same thing to
// click wherever it is drawn. Only the extra columns differ — score, margin and
// placement are things a CLASSIFIER knows about an answer, and a rating of 8
// has none of them, so those canvases pass no extra columns rather than
// printing three columns of dashes.

const PAGE = 100;

const DEFAULT_TIPS = {
    recipient: 'Who gave this answer. Opens their session in Explore.',
    response: 'The answer this respondent gave.',
};

// The answers behind a bucket, in the shape this table renders. Every canvas
// selects its own bucket its own way; what an answer looks like once selected
// is one rule, here.
export function toDrillRows(rows = []) {
    return rows.map((row, index) => ({
        key: row.sessionId || index,
        recipientId: row.recipientId,
        sessionId: row.sessionId,
        response: row.value,
    }));
}

// Who gave this answer, and a way straight to their session. An author reading
// a bucket wants to reach the person behind an answer — the chip opens that
// recipient's session in Explore, where the full exchange is shown.
// The recipient id, as a link to that recipient's answers in Explore.
//
// It is the SAME id the SRA results table is keyed by: both resolve
// sessions.participant_id to recipients.recipient_id, so what an author reads
// here is what they will find there.
//
// A take with no recipient at all — an anonymous browser response — has no id
// to show. It said "Session only", which named what was missing instead of
// what the answer is. It is simply anonymous, and it still links, because the
// session holds that person's other answers even when nothing names them.
export function RecipientChip({ recipientId, sessionId, onOpenSession }) {
    if (!recipientId && !sessionId) return <span style={{ color: '#9AA0A6' }}>Anonymous</span>;
    const label = recipientId || 'Anonymous';
    const title = recipientId
        ? `Open recipient ${recipientId} in Explore`
        : 'This response was given anonymously — no recipient is recorded. Opens the session in Explore.';
    if (!onOpenSession || !sessionId) {
        return <span style={{ ...chipStyle, cursor: 'default' }} title={title}>{label}</span>;
    }
    return (
        <button
            type="button"
            onClick={() => onOpenSession(sessionId)}
            title={title}
            style={{ ...chipStyle, cursor: 'pointer', color: '#1558B0', borderColor: '#C6DAFC' }}
        >
            {label}
        </button>
    );
}

/**
 * @param {string} label        the bucket that was clicked, named as the chart names it
 * @param {Array} rows          [{ key, recipientId, sessionId, response, cells? }]
 * @param {Array} columns       extra column headers beyond Recipient and Response
 * @param {Function} onClose    clears the drill selection in the owning chart
 */
export default function ResponseDrillTable({
    label,
    rows = [],
    columns = [],
    onClose,
    onOpenSession,
    recipientTip = DEFAULT_TIPS.recipient,
    responseTip = DEFAULT_TIPS.response,
}) {
    const [limit, setLimit] = useState(PAGE);
    // A new bucket starts at the top of its own list rather than inheriting how
    // far the author had paged through the last one.
    useEffect(() => { setLimit(PAGE); }, [label]);

    return (
        <div style={{ marginTop: 16, border: '1px solid #E8EAED', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', backgroundColor: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E8EAED' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#202124' }}>{label} — {rows.length} response{rows.length === 1 ? '' : 's'}</span>
                <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#5F6368' }}>×</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr style={{ color: '#5F6368' }}>
                            <th title={recipientTip} style={thStyle}>Recipient</th>
                            <th title={responseTip} style={thStyle}>Response</th>
                            {columns.map(column => <th key={column.header} title={column.title} style={thStyle}>{column.header}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, limit).map((row, index) => (
                            <tr key={row.key ?? row.sessionId ?? index} style={{ borderTop: '1px solid #F1F3F4', color: '#202124' }}>
                                <td style={tdStyle}>
                                    <RecipientChip recipientId={row.recipientId} sessionId={row.sessionId} onOpenSession={onOpenSession} />
                                </td>
                                <td style={{ ...tdStyle, maxWidth: 320 }}>{row.response}</td>
                                {(row.cells || []).map((cell, cellIndex) => <td key={columns[cellIndex]?.header ?? cellIndex} style={tdStyle}>{cell}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > limit && (
                <div style={{ padding: 8, textAlign: 'center', borderTop: '1px solid #E8EAED' }}>
                    <button onClick={() => setLimit(value => value + PAGE)} style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #DADCE0', borderRadius: 4, background: '#FFFFFF', color: '#2B6BBF', cursor: 'pointer' }}>
                        Show more ({rows.length - limit} remaining)
                    </button>
                </div>
            )}
        </div>
    );
}

const thStyle = { textAlign: 'left', padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 10px', verticalAlign: 'top' };
// The recipient is shown WHOLE. Truncating it to an ellipsis left an author
// unable to read or copy who answered, so finding that person in Explore meant
// searching by the text of their answer instead. It wraps rather than clips.
const chipStyle = { display: 'inline-block', textAlign: 'left', overflowWrap: 'anywhere', fontSize: 12, lineHeight: 1.4, padding: '2px 8px', borderRadius: 10, border: '1px solid #DADCE0', background: '#FFFFFF', color: '#3C4043' };
